import React, { createContext, useContext, useEffect, useState } from "react";
import { api, loginRequest } from "@/lib/api";
import {
  clearSession,
  ensureValidToken,
  getCachedUser,
  getRefreshToken,
  getToken,
  isTokenExpired,
  saveSession,
} from "@/lib/authStorage";

const AuthCtx = createContext(null);

export { ensureValidToken };

export function AuthProvider({ children }) {
  const [user, setUser] = useState(getCachedUser);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const token = getToken();
      if (!token) {
        setUser(null);
        setLoading(false);
        return;
      }
      if (window.location.pathname === "/login") {
        clearSession();
        setUser(null);
        setLoading(false);
        return;
      }
      try {
        await ensureValidToken();
        const r = await api.get("/auth/me");
        if (cancelled) return;
        saveSession({ user: r.data });
        setUser(r.data);
      } catch {
        if (!cancelled) {
          clearSession();
          setUser(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = async (username, password) => {
    const data = await loginRequest(username, password);
    saveSession({
      token: data.token,
      refresh_token: data.refresh_token,
      expires_at: data.expires_at,
      user: data.user,
    });
    setUser(data.user);
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      // Local session cleanup still runs if the network request fails.
    }
    clearSession();
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
